import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Plus, Search, HelpCircle, Archive, Eye, X, MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { CreateBidSheet } from "./CreateBidSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { LoadingSpinner } from "@/components/loading-spinner";
import { ProtectByPermission } from "@/components/auth";
import { EmptyState } from "@/components/ui/empty-state";

// Revenue range filter options
const REVENUE_RANGES = [
  { value: "under_1000", label: "Under $1,000", min: 0, max: 999.99 },
  { value: "1000_5000", label: "$1,000 - $5,000", min: 1000, max: 5000 },
  { value: "5000_10000", label: "$5,000 - $10,000", min: 5000.01, max: 10000 },
  { value: "10000_25000", label: "$10,000 - $25,000", min: 10000.01, max: 25000 },
  { value: "25000_plus", label: "$25,000+", min: 25000.01, max: Infinity },
];

interface Bid {
  _id: string;
  title: string;
  client: {
    company: string;
  };
  status: string;
  value: string;
  lastModified: string;
  monthlyRevenue: number;
}

export default function BidList() {
  const navigate = useNavigate();
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [revenueFilter, setRevenueFilter] = useState<string | null>(null);

  // Fetch bids from Convex
  const bids = useQuery(api.bids.list);

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || revenueFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setRevenueFilter(null);
  };

  // Filter bids based on search query, status, and revenue range
  const filteredBids = Array.isArray(bids)
    ? bids.filter((bid) => {
        // Apply search filter
        const matchesSearch =
          searchQuery === "" ||
          bid.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          bid.client.company.toLowerCase().includes(searchQuery.toLowerCase());

        // Apply status filter
        const matchesStatus = statusFilter === null || bid.status === statusFilter;

        // Apply revenue filter
        let matchesRevenue = true;
        if (revenueFilter !== null) {
          const range = REVENUE_RANGES.find((r) => r.value === revenueFilter);
          if (range) {
            matchesRevenue = bid.monthlyRevenue >= range.min && bid.monthlyRevenue <= range.max;
          }
        }

        return matchesSearch && matchesStatus && matchesRevenue;
      })
    : [];

  // Show loading state while fetching data
  if (bids === undefined || bids === null) {
    return (
      <DashboardLayout activeTab="bids">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="bids">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bids</h1>
            <p className="mt-1 text-gray-500">
              Manage and track your bid proposals, scope of work and contracts
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={() => navigate("/bids/archived")}>
              <Archive className="h-4 w-4" /> Archived Bids
            </Button>
            <ProtectByPermission permission="bids:write">
              <Button className="gap-2" onClick={() => setIsCreateSheetOpen(true)}>
                <Plus className="h-4 w-4" /> New Bid
              </Button>
            </ProtectByPermission>
          </div>
        </div>

        <Card className="p-6">
          <div className="mb-6 flex flex-col gap-4">
            {/* Search and filters row */}
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search bids..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter || "all"}
                onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Submitted">Submitted</SelectItem>
                  <SelectItem value="In Review">In Review</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={revenueFilter || "all"}
                onValueChange={(value) => setRevenueFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by revenue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Revenue</SelectItem>
                  {REVENUE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    Monthly Revenue
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 cursor-help text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>
                            The recommended monthly revenue for this bid based on the pricing
                            calculator. This represents the expected monthly income from the
                            contract.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableHead>
                <TableHead>Last Modified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBids.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState
                      icon={FileText}
                      title="No bids yet"
                      description="Create your first bid to get started."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredBids.map((bid) => (
                  <TableRow key={bid._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        {bid.title}
                      </div>
                    </TableCell>
                    <TableCell>{bid.client.company}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          bid.status === "Approved"
                            ? "bg-green-100 text-green-700"
                            : bid.status === "Submitted"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {bid.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(bid.monthlyRevenue)}
                    </TableCell>
                    <TableCell>{new Date(bid.lastModified).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/bids/${bid._id}`, { state: { from: "/bids" } })}>
                        <Eye className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
      <CreateBidSheet isOpen={isCreateSheetOpen} onClose={() => setIsCreateSheetOpen(false)} />
    </DashboardLayout>
  );
}
